import { useState } from "react";
// outsource
import { Row, Col, Button, InputNumber, Table } from "antd";
import { InfoCircleFilled } from "@ant-design/icons";

const formatRoundTo = (n) => (n === 0 ? "0 minute" : `+${n} minutes`);

const PayoutSettingRoundingSection = ({ data, examples, onSave }) => {
  // -------------------------- STATE --------------------------
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  // -------------------------- FUNCTION --------------------------
  const handleEdit = () => {
    setDraft({ ...data, rules: data.rules.map((r) => ({ ...r })) });
    setEditing(true);
  };

  const handleCancel = () => setEditing(false);

  const handleSave = () => {
    onSave(draft);
    setEditing(false);
  };

  const handleRuleChange = (key, field, value) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    }));
  };

  // -------------------------- VAR --------------------------
  const ruleColumns = [
    {
      title: "From (min)",
      dataIndex: "from",
      key: "from",
      width: 120,
      render: (val, record) =>
        editing ? (
          <InputNumber
            value={draft.rules?.find((r) => r.key === record.key)?.from ?? val}
            min={0}
            className="ps-rule-input"
            onChange={(v) => handleRuleChange(record.key, "from", v)}
          />
        ) : val,
    },
    {
      title: "To (min)",
      dataIndex: "to",
      key: "to",
      width: 120,
      render: (val, record) =>
        editing ? (
          <InputNumber
            value={draft.rules?.find((r) => r.key === record.key)?.to ?? val}
            min={0}
            className="ps-rule-input"
            onChange={(v) => handleRuleChange(record.key, "to", v)}
          />
        ) : val,
    },
    {
      title: "Round To",
      dataIndex: "roundTo",
      key: "roundTo",
      render: (val, record) =>
        editing ? (
          <InputNumber
            value={draft.rules?.find((r) => r.key === record.key)?.roundTo ?? val}
            min={0}
            className="ps-rule-input"
            onChange={(v) => handleRuleChange(record.key, "roundTo", v)}
          />
        ) : formatRoundTo(val),
    },
  ];

  const exampleColumns = [
    { title: "Actual Time", dataIndex: "actual", key: "actual" },
    { title: "Rounded Time", dataIndex: "rounded", key: "rounded", align: "right" },
  ];

  // -------------------------- RENDER --------------------------
  const renderHeader = () => (
    <>
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <div className="ps-rounding-header-left">
            <strong>Rounding Rules</strong>
            <div className="ps-info-box ps-rounding-info-box">
              <InfoCircleFilled className="ps-info-icon" />
              <div>
                Rules are applied only on the first login of each day.
              </div>
            </div>
          </div>
        </Col>
        <Col>
          <Row gutter={[8, 0]}>
            {editing ? (
              <>
                <Col>
                  <Button size="small" className="ps-cancel-btn" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Col>
                <Col>
                  <Button type="primary" size="small" className="ps-edit-btn" onClick={handleSave}>
                    Save
                  </Button>
                </Col>
              </>
            ) : (
              <Col>
                <Button type="primary" size="small" className="ps-edit-btn" onClick={handleEdit}>
                  Edit
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </>
  );

  const renderBody = () => (
    <Row gutter={[0, 0]} style={{ marginTop: 8 }}>
      <Col xl={12} lg={12} md={24} xs={24} className="ps-rounding-left">
        <div className="ps-base-interval">
          Base Interval:{" "}
          {editing ? (
            <InputNumber
              value={draft.baseInterval}
              min={1}
              max={60}
              className="ps-rule-input"
              onChange={(v) => setDraft((prev) => ({ ...prev, baseInterval: v }))}
            />
          ) : (
            <><strong>{data?.baseInterval}</strong> </>
          )}{" "}
          minutes
        </div>
        <Table
          columns={ruleColumns}
          dataSource={editing ? draft.rules : data?.rules}
          pagination={false}
          size="small"
          bordered={false}
          rowKey="key"
        />
      </Col>
      <Col xl={12} lg={12} md={24} xs={24} className="ps-rounding-right">
        <div className="ps-example-title">Example:</div>
        <Table
          columns={exampleColumns}
          dataSource={examples}
          pagination={false}
          size="small"
          bordered={false}
          rowKey="key"
        />
      </Col>
    </Row>
  );

  // -------------------------- MAIN --------------------------
  return (
    <div className="ps-section">
      {renderHeader()}
      {renderBody()}
    </div>
  );
};

export default PayoutSettingRoundingSection;
